import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import { PersistGate } from 'redux-persist/integration/react';
import { FeatureToggleProvider } from 'components/feature-toggle';
import { GlobalStyles as ORCGlobalStyles, OrcProvider } from '@ontruck/orc';
import { ReactComponent as OrcIcons } from '@ontruck/orc/lib/orc.svg';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';

import { actions as authActions } from 'state/auth';
import api from 'api';
import getStore from 'state/store';
import { GlobalStyles, themeBaseOverwrite } from 'utils/style';
import App from 'pages/App';

import { FormStateProvider } from 'hooks/useFormState';
import { FeatureFlagProvider } from 'hooks/useFeatureFlags';
import { resetLoadsCache } from 'refactored/features/form/loads/loadsCache';
import OntruckExtensionReceiver from 'refactored/features/ontruckExtension/OntruckExtensionReceiver';
import { addHeaderTags, addTitle } from '../assets/icon_by_config/HeaderConfig';

import { getCurrentTenant18n } from '../i18n';
const { store, persistor, history } = getStore();

api.setCallbacks({
  onRenewTokenSuccess: (credentials) => {
    store.dispatch(authActions.setCredentials(credentials));
  },
});

const Root = ({ children }) => {
  addTitle();
  addHeaderTags();
  const i18nProvider = getCurrentTenant18n();
  resetLoadsCache();
  return (
    <Sentry.ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <I18nextProvider i18n={i18nProvider}>
            <FeatureFlagProvider>
              <FormStateProvider>
                <FeatureToggleProvider>
                  <OrcProvider themeBase={themeBaseOverwrite}>
                    <>
                      {children || <App history={history} />}
                      <OrcIcons />
                      <ORCGlobalStyles />
                      <GlobalStyles />
                      <OntruckExtensionReceiver />
                      <Toaster richColors />
                    </>
                  </OrcProvider>
                </FeatureToggleProvider>
              </FormStateProvider>
            </FeatureFlagProvider>
          </I18nextProvider>
        </PersistGate>
      </Provider>
    </Sentry.ErrorBoundary>
  );
};

Root.propTypes = {
  children: PropTypes.node,
};

Root.defaultProps = {
  children: undefined,
};

export { store };
export default Root;
